/**
 * windowVisibility — single source of truth for "is the app window visible?".
 *
 * Combines the two signals the app already trusts for presence:
 *   1. Electron `onWindowVisibilityChange` IPC — authoritative on desktop.
 *      Fires on BrowserWindow minimize / hide-to-tray / restore / show.
 *      Applied IMMEDIATELY (the main process knows the state before any
 *      renderer-side throttling kicks in).
 *   2. `document.visibilitychange` — web fallback / backup. Debounced 200ms to
 *      absorb quick tab-switch flicker.
 *
 * Consumed by:
 *   - useWindowPresence — instant 'online'/'away' push on each change.
 *   - useChatWs — tags every heartbeat with the current visibility so the
 *     server-side idle sweeper can flip a stale (minimized) client to 'away'
 *     even if the one-shot push frame was lost.
 *
 * Singleton: starts tracking on first import and stays for the app lifetime.
 */

const DEBOUNCE_MS = 200;

type Listener = (visible: boolean) => void;

const dlog = (msg: string) => {
  try {
    (window as Window).electronAPI?.logPresence?.(msg);
  } catch {
    /* noop */
  }
};

let _visible =
  typeof document !== 'undefined'
    ? document.visibilityState === 'visible'
    : true;
let _started = false;
let _timer: ReturnType<typeof setTimeout> | undefined;
const _listeners = new Set<Listener>();

function _apply(next: boolean): void {
  if (next === _visible) return;
  _visible = next;
  for (const l of _listeners) {
    try {
      l(_visible);
    } catch {
      /* a bad listener must not break the others */
    }
  }
}

function _applyImmediate(next: boolean): void {
  if (_timer) {
    clearTimeout(_timer);
    _timer = undefined;
  }
  _apply(next);
}

function _applyDebounced(next: boolean): void {
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => {
    _timer = undefined;
    _apply(next);
  }, DEBOUNCE_MS);
}

function _ensureStarted(): void {
  if (_started) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  _started = true;

  document.addEventListener('visibilitychange', () => {
    dlog(`windowVisibility: visibilitychange → ${document.visibilityState}`);
    _applyDebounced(document.visibilityState === 'visible');
  });

  const electronAPI = (window as Window).electronAPI;
  electronAPI?.onWindowVisibilityChange?.((v) => {
    dlog(`windowVisibility: electron IPC → ${v}`);
    _applyImmediate(v === 'visible');
  });

  // Pull the authoritative CURRENT state once on start. The main process only
  // EMITS visibility on transitions (minimize/hide/restore/show), so after an
  // auto-reload that happened while minimized no event fires and the
  // document-based default above can be wrong ('visible' for a hidden window).
  // Asking for the live state corrects a stuck-online client after such a
  // reload. Best-effort: falls back to the document/default on any failure.
  const visibilityPull = electronAPI?.getWindowVisibility?.();
  if (visibilityPull) {
    visibilityPull
      .then((v) => {
        dlog(`windowVisibility: electron pull → ${v}`);
        _applyImmediate(v === 'visible');
      })
      .catch(() => {
        /* best-effort — keep the document/default seed */
      });
  }
}

// Begin tracking as soon as the module is imported in a renderer/browser.
_ensureStarted();

/** Current window visibility. Cheap synchronous read (safe in a heartbeat). */
export function isWindowVisible(): boolean {
  return _visible;
}

/** Subscribe to visibility changes. Returns an unsubscribe function. */
export function subscribeWindowVisibility(listener: Listener): () => void {
  _ensureStarted();
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}
